import React, { useState, useEffect } from "react";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
    Book, Atom, Calculator, PenTool, TestTube, Leaf, FunctionSquare,
    Sprout, TrendingUp, Cross, Microscope, Monitor, Globe, Users,
    Heart, Briefcase, Palette, Home, Shield, BookOpen, FileText,
    BarChart3, Building2, Coffee, Activity, Target, Gamepad2,
    Zap, Plus, TreePine
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const subjectIcons = {
    "Physics": Atom,
    "Chemistry": TestTube,
    "Biology": Leaf,
    "Further Mathematics": FunctionSquare,
    "Mathematics": Calculator,
    "Agricultural Science": Sprout,
    "Commerce": TrendingUp,
    "CRS": Cross,
    "Christian Religious Studies (CRS)": Cross,
    "English Language": PenTool,
    "English Studies": Book,
    "Basic Science": Microscope,
    "Basic Technology": Monitor,
    "Social Studies": Globe,
    "Civic Education": Users,
          "Computer Science": Monitor,
    "Physical and Health Education": Heart,
    "Physical and Health Education (PHE)": Heart,
    "Business Studies": Briefcase,
    "Creative Arts": Palette,
    "Home Economics": Home,
    "Security Education": Shield,
    "Literature": BookOpen,
    "Geography": Globe,
    "History": FileText,
    "Economics": BarChart3,
    "Government": Building2,
    "French": Coffee,
    "Arabic": Book,
    "Hausa": Book,
    "Igbo": Book,
    "Yoruba": Book,
    "Fine Arts": Palette,
    "Music": Activity,
    "Technical Drawing": Target,
    "Auto Mechanics": Gamepad2,
    "Electronics": Zap,
    "Building Construction": Building2,
    "Metal Work": Plus,
    "Wood Work": TreePine,
    "Food and Nutrition": Home,
    "Clothing and Textiles": Home
};

const COLORS = ["#4F46E5", "#22C55E", "#F97316", "#EC4899", "#8B5CF6", "#06B6D4", "#F59E0B", "#EF4444"];

export default function StudentSubjectAnalysis() {
    const [userData, setUserData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        gender: "",
        dob: "",
        country: "",
        state: "",
        accountType: "",
        yearGroup: "",
        school: "",
        subjects: "[]",
        guardianEmail: "",
        isVerified: false,
        subscribed: false
    });
    const [scoreData, setScoreData] = useState([]);
    const navigate = useNavigate();
    const [progressData, setProgressData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [examData, setExamData] = useState([]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, []); // runs only on mount

    // Parse subjects from string
    const parseSubjects = (subjectsString) => {
        try {
            return JSON.parse(subjectsString || "[]");
        } catch {
            return [];
        }
    };

    // Calculate subject performance from exam data
    const calculateSubjectPerformance = (examResults) => {
        const subjectStats = {};

        examResults.forEach(exam => {
            const subject = exam.subject;
            if (!subjectStats[subject]) {
                subjectStats[subject] = {
                    totalQuestions: 0,
                    correctAnswers: 0,
                    attempts: []
                };
            }

            subjectStats[subject].totalQuestions += 1;
            subjectStats[subject].correctAnswers += exam.is_correct;
            subjectStats[subject].attempts.push({
                date: exam.createdAt,
                isCorrect: exam.is_correct,
                topic: exam.topic
            });
        });

        return Object.entries(subjectStats).map(([subject, stats]) => ({
            subject,
            score: Math.round((stats.correctAnswers / stats.totalQuestions) * 100),
            totalQuestions: stats.totalQuestions,
            correctAnswers: stats.correctAnswers,
            attempts: stats.attempts
        }));
    };

    // Generate progress data based on chronological exam performance
    const generateProgressFromExamData = (examResults) => {
        if (examResults.length === 0) return [];

        // Sort exams by date
        const sortedExams = [...examResults].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        // Group by subject
        const subjectData = {};
        sortedExams.forEach(exam => {
            if (!subjectData[exam.subject]) {
                subjectData[exam.subject] = [];
            }
            subjectData[exam.subject].push(exam);
        });

        // Create progress data for each subject across time periods
        const subjects = Object.keys(subjectData);
        const progressPoints = [];

        // Create 4 time periods based on exam data
        const totalExams = sortedExams.length;
        const quarterSize = Math.ceil(totalExams / 4);

        for (let i = 0; i < 4; i++) {
            const periodLabel = `Period ${i + 1}`;
            const periodData = { week: periodLabel };

            subjects.forEach(subject => {
                const subjectExams = subjectData[subject];
                const startIdx = Math.floor((i / 4) * subjectExams.length);
                const endIdx = Math.floor(((i + 1) / 4) * subjectExams.length);
                const periodExams = subjectExams.slice(startIdx, endIdx);

                if (periodExams.length > 0) {
                    const correctCount = periodExams.reduce((sum, exam) => sum + exam.is_correct, 0);
                    const percentage = Math.round((correctCount / periodExams.length) * 100);
                    periodData[subject] = percentage;
                } else {
                    // If no data for this period, interpolate or use previous value
                    periodData[subject] = i > 0 ? progressPoints[i - 1][subject] || 0 : 0;
                }
            });

            progressPoints.push(periodData);
        }

        return progressPoints;
    };

    // Fetch exam analysis data
    useEffect(() => {
        const fetchExams = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/tests/analysis`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch exam data");
                }

                const exams = await response.json();
                console.log(exams.data);
                setExamData(exams.data);

                // Calculate subject performance from exam data
                if (exams.data && exams.data.length > 0) {
                    const subjectPerformance = calculateSubjectPerformance(exams.data);
                    setScoreData(subjectPerformance);

                    // Generate progress data from exam results
                    const progress = generateProgressFromExamData(exams.data);
                    setProgressData(progress);
                }

            } catch (error) {
                console.error("Error fetching exam data:", error.message);
            }
        };

        fetchExams();
    }, []);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/users/me`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch user profile");
                }

                const userDataResponse = await response.json();
                const filteredData = {
                    firstName: userDataResponse.firstName || "",
                    lastName: userDataResponse.lastName || "",
                    email: userDataResponse.email || "",
                    phoneNumber: userDataResponse.phoneNumber || "",
                    gender: userDataResponse.gender || "",
                    dob: userDataResponse.dob || "",
                    country: userDataResponse.country || "",
                    state: userDataResponse.state || "",
                    accountType: userDataResponse.accountType || "",
                    yearGroup: userDataResponse.yearGroup || "",
                    school: userDataResponse.school || "",
                    subjects: userDataResponse.subjects || "[]",
                    guardianEmail: userDataResponse.guardianEmail || "",
                    isVerified: userDataResponse.isVerified || false,
                    subscribed: userDataResponse.subscribed || false
                };

                setUserData(filteredData);

            } catch (error) {
                console.error("Error fetching user profile:", error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const subjects = parseSubjects(userData.subjects);

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-800">Loading Analysis...</h2>
                    <p className="text-gray-600">Fetching your subject data</p>
                </div>
            </div>
        );
    }

    if (scoreData.length === 0) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4 text-gray-800">No Exam Data Found</h1>
                    <p className="text-gray-600">Please take some exams to view your performance analysis.</p>
                </div>
            </div>
        );
    }

    const getSubjectIcon = (subject) => {
        const IconComponent = subjectIcons[subject];
        return IconComponent ? <IconComponent className="w-5 h-5" /> : <Book className="w-5 h-5 text-gray-500" />;
    };

    const getSubjectColor = (index) => COLORS[index % COLORS.length];

    const goBack = () => {
        navigate(-1);
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="flex items-center mb-6">
                <button
                    onClick={goBack}
                    className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                >
                    <FaArrowLeft className="text-xl" />
                    Back
                </button>
            </div>

            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Subject Analysis Dashboard</h1>
                <p className="text-gray-600 mt-1">
                    Exam performance analysis for {userData.firstName} {userData.lastName}
                    {userData.school && ` • ${userData.school}`}
                    {userData.yearGroup && ` • ${userData.yearGroup}`}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                    Based on {examData.length} exam questions across {scoreData.length} subjects
                </p>
            </div>

            {/* Subject Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
                {scoreData.map((item, index) => (
                    <div
                        key={item.subject}
                        className="flex flex-col items-center bg-white p-4 rounded-xl shadow hover:shadow-lg transition"
                    >
                        <div style={{ color: getSubjectColor(index) }}>
                            {getSubjectIcon(item.subject)}
                        </div>
                        <span className="mt-2 text-xs font-medium text-center leading-tight">{item.subject}</span>
                        <span className="text-lg font-bold text-gray-700">{item.score}%</span>
                        <span className="text-xs text-gray-500">{item.correctAnswers}/{item.totalQuestions}</span>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                                className="h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${item.score}%`,
                                    backgroundColor: getSubjectColor(index)
                                }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Bar Chart: Overall Performance */}
                <div className="bg-white p-6 rounded-xl shadow">
                    <h2 className="text-lg font-semibold mb-4">Overall Exam Performance</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={scoreData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="subject"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                fontSize={10}
                            />
                            <YAxis />
                            <Tooltip formatter={(value, name) => [`${value}%`, 'Score']} />
                            <Bar
                                dataKey="score"
                                radius={[4, 4, 0, 0]}
                                fill={(entry, index) => getSubjectColor(index)}
                            >
                                {scoreData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={getSubjectColor(index)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Pie Chart: Score Distribution */}
                <div className="bg-white p-6 rounded-xl shadow">
                    <h2 className="text-lg font-semibold mb-4">Score Distribution</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={scoreData}
                                dataKey="score"
                                nameKey="subject"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label={({ subject, score }) => `${score}%`}
                            >
                                {scoreData.map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={getSubjectColor(index)} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [`${value}%`, name]} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Multi-Line Chart: Progress for all subjects */}
                <div className="bg-white p-6 rounded-xl shadow lg:col-span-2">
                    <h2 className="text-lg font-semibold mb-4">Progress Over Time</h2>
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={progressData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="week" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            {scoreData.map((item, index) => (
                                <Line
                                    key={item.subject}
                                    type="monotone"
                                    dataKey={item.subject}
                                    stroke={getSubjectColor(index)}
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                    activeDot={{ r: 6 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Statistics Summary */}
                <div className="bg-white p-6 rounded-xl shadow lg:col-span-2">
                    <h2 className="text-lg font-semibold mb-4">Performance Summary</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">
                                {scoreData.length > 0 ? Math.round(scoreData.reduce((sum, item) => sum + item.score, 0) / scoreData.length) : 0}%
                            </div>
                            <div className="text-sm text-gray-600">Average Score</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">
                                {scoreData.length > 0 ? Math.max(...scoreData.map(item => item.score)) : 0}%
                            </div>
                            <div className="text-sm text-gray-600">Highest Score</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <div className="text-2xl font-bold text-orange-600">
                                {scoreData.length > 0 ? Math.min(...scoreData.map(item => item.score)) : 0}%
                            </div>
                            <div className="text-sm text-gray-600">Lowest Score</div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">
                                {examData.reduce((sum, exam) => sum + exam.is_correct, 0)} / {examData.length}
                            </div>
                            <div className="text-sm text-gray-600">Total Correct</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}